import { Component, Suspense, lazy, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from '@/contexts/AuthContext'
import { EntityProvider } from '@/contexts/EntityContext'
import { AuthenticatedLayout } from '@/layouts/AuthenticatedLayout'
import { useDarkMode } from '@/hooks/useDarkMode'
import { CookieConsentBanner } from '@/components/CookieConsentBanner'
import { isNative } from '@/platform'
import { TWINT_RETURN_ROUTE } from '@/platform/twint'

// Rails mounts the SPA at `/app` (vite-plugin-ruby renders the entry there).
// Capacitor WebView serves from `https://localhost/index.html` with no prefix.
// Use the matching basename in each context so routes resolve correctly.
const ROUTER_BASENAME = isNative() ? '/' : '/app'

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, fontFamily: 'monospace', color: '#dc2626', background: '#fff' }}>
          <h1>Runtime Error</h1>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{this.state.error.message}</pre>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#666', marginTop: 8 }}>{this.state.error.stack}</pre>
        </div>
      )
    }
    return this.props.children
  }
}

// Every page is lazy-loaded so each route ships its own chunk. The
// Suspense fallback below renders a tiny spinner while the route chunk
// streams in (~50-200 ms on a warm CDN). The main chunk only contains
// the router, contexts, layout shell, and bottom-tab bar.
const HomePage = lazy(() => import('@/pages/HomePage'))
const DiscoverPage = lazy(() => import('@/pages/DiscoverPage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'))
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'))
const AssociationsPage = lazy(() => import('@/pages/AssociationsPage'))
const MembersPage = lazy(() => import('@/pages/MembersPage'))
const GovernancePage = lazy(() => import('@/pages/GovernancePage'))
const CirclesPage = lazy(() => import('@/pages/CirclesPage'))
const TwintReturnPage = lazy(() => import('@/pages/TwintReturnPage'))
const MessagesPage = lazy(() => import('@/pages/MessagesPage'))
const EventsPage = lazy(() => import('@/pages/EventsPage'))
const EventCreatorPage = lazy(() => import('@/pages/EventCreatorPage'))
const B2bPage = lazy(() => import('@/pages/B2bPage'))
const AdminPage = lazy(() => import('@/pages/AdminPage'))
const NetworkGraphPage = lazy(() => import('@/pages/NetworkGraphPage'))
const BusinessRelationshipsDashboardPage = lazy(() => import('@/pages/BusinessRelationshipsDashboardPage'))
const ContractReviewPage = lazy(() => import('@/pages/ContractReviewPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const CommunityPage = lazy(() => import('@/pages/CommunityPage')) // M-A1 — un-deferred
const CreditPage = lazy(() => import('@/pages/CreditPage')) // M-A2 — un-deferred
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const FinancesPage = lazy(() => import('@/pages/FinancesPage'))
const TreasuryPage = lazy(() => import('@/pages/TreasuryPage'))
const HelpPage = lazy(() => import('@/pages/HelpPage'))

// Suspense fallback for in-flight route chunks. Minimal so it shows
// instantly without competing with the splash screen.
function RouteLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  )
}

function AppRoutes() {
  useDarkMode()

  return (
    <Suspense fallback={<RouteLoader />}>
    <Routes>
      {/* Public routes — no shell */}
      <Route index element={<HomePage />} />
      <Route path="login" element={<LoginPage />} />
      <Route path="register" element={<RegisterPage />} />
      {/* M-A1 — referral invite links resolve to /join?ref=CODE; render the
          register page, which reads ?ref and attributes the signup. */}
      <Route path="join" element={<RegisterPage />} />
      <Route path="forgot-password" element={<ForgotPasswordPage />} />
      <Route path="verify-email" element={<VerifyEmailPage />} />
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="onboarding" element={<OnboardingPage />} />

      {/* Authenticated routes — with shell */}
      <Route element={<AuthenticatedLayout />}>
        <Route path="homepage" element={<Navigate to="/discover" replace />} />
        <Route path="associations" element={<AssociationsPage />} />
        <Route path="members" element={<MembersPage />} />
        <Route path="circles" element={<CirclesPage />} />
        {/* Where Stripe returns a WEB TWINT payer after they authorise. The
            path comes from platform/twint.ts, which also builds the return URL
            handed to Stripe — one constant, so the two cannot drift apart as
            they had (the route was missing entirely and the redirect rendered
            nothing). Native returns via the `circleup://twint/return` deep link
            and never reaches this route. */}
        <Route path={TWINT_RETURN_ROUTE} element={<TwintReturnPage />} />
        <Route path="discover" element={<DiscoverPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="events/create" element={<EventCreatorPage />} />
        <Route path="b2b" element={<B2bPage />} />
        <Route path="relationships" element={<BusinessRelationshipsDashboardPage />} />
        <Route path="contract-review/:token" element={<ContractReviewPage />} />
        <Route path="network" element={<NetworkGraphPage />} />
        <Route path="admin" element={<AdminPage />} />

        {/* V2+ routes — redirect to discover for MVP */}
        <Route path="finances" element={<FinancesPage />} />
        {/* P-E2 — GovernancePage was real (live useGovernance: elections,
            proposals, voting booth) but dead-routed to /discover since MVP.
            Revived so P-B5's governance notifications have somewhere to land. */}
        <Route path="governance" element={<GovernancePage />} />
        <Route path="governance/vote/:id" element={<Navigate to="/governance?view=voting-booth" replace />} />
        <Route path="documents" element={<Navigate to="/discover" replace />} />
        <Route path="projects" element={<Navigate to="/discover" replace />} />
        <Route path="projects/:id/donate" element={<Navigate to="/discover" replace />} />
        <Route path="community" element={<CommunityPage />} />
        <Route path="credit" element={<CreditPage />} />{/* M-A2 — un-deferred (inside the app shell) */}
        <Route path="analytics" element={<Navigate to="/discover" replace />} />
        <Route path="federations" element={<Navigate to="/discover" replace />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="help" element={<HelpPage />} />
      </Route>

      {/* Backward-compat redirects */}
      {/* T-B1 — revived: the cross-circle treasurer portfolio dashboard now has
          a real backend (treasury_portfolio) after being a dead-routed stub. */}
      <Route path="treasury" element={<TreasuryPage />} />
      <Route path="multi-share" element={<Navigate to="/discover" replace />} />
      <Route path="insights" element={<Navigate to="/discover" replace />} />
      <Route path="communication" element={<Navigate to="/messages" replace />} />

      {/* Catch-all. Without it an unmatched path rendered a blank page with no
          error — which is exactly how the missing TWINT return route stayed
          invisible. A redirect is better than a blank screen, and a wrong URL
          should never look like a broken app. */}
      <Route path="*" element={<Navigate to="/discover" replace />} />
    </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter basename={ROUTER_BASENAME}>
        <AuthProvider>
          <EntityProvider>
            <AppRoutes />
            <CookieConsentBanner />
          </EntityProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
